import {FC, useEffect, useRef} from 'react';
import {Box, Button, Flex, Stack} from '@chakra-ui/react';
import {api, useT} from '@kcast-fe/core';
import {Controller, useForm} from 'react-hook-form';
import {Input, Switch, useScreenSize} from 'ui-kit';

interface IProps {
  onSaved: () => void;
}

interface INotesSettingsForm {
  max_notes: number;
  soft_limit: number;
  max_note_length: number;
  ttl_enabled: boolean;
  ttl_hours: number;
  enabled: boolean;
}

const DEFAULTS: INotesSettingsForm = {
  max_notes: 0,
  soft_limit: 0,
  max_note_length: 0,
  ttl_enabled: false,
  ttl_hours: 0,
  enabled: false
};

const NotesSettingsTabFC: FC<IProps> = ({onSaved}) => {
  const {data: settings, isLoading} = api.settings.useSettingsQuery();
  const updateMutation = api.settings.useUpdateMemoryNotesMutation();

  const isInit = useRef(false);

  const {register, control, watch, handleSubmit, reset, formState} = useForm<INotesSettingsForm>({
    defaultValues: DEFAULTS
  });

  const ttlEnabled = watch('ttl_enabled');

  const {isLessThan768} = useScreenSize();
  const {t} = useT();

  useEffect(() => {
    if (settings?.memory_notes && !isInit.current) {
      isInit.current = true;
      reset({
        max_notes: settings.memory_notes.max_notes,
        soft_limit: settings.memory_notes.soft_limit,
        max_note_length: settings.memory_notes.max_note_length,
        ttl_enabled: settings.memory_notes.ttl_enabled,
        ttl_hours: settings.memory_notes.ttl_hours,
        enabled: settings.memory_notes.enabled
      });
    }
  }, [settings, reset]);

  const onSubmit = (data: INotesSettingsForm) => {
    updateMutation.mutate(
      {
        max_notes: data.max_notes,
        soft_limit: data.soft_limit,
        max_note_length: data.max_note_length,
        ttl_enabled: data.ttl_enabled,
        ttl_hours: data.ttl_hours,
        enabled: data.enabled
      },
      {
        onSuccess: () => {
          reset(data);
          onSaved();
        }
      }
    );
  };

  if (isLoading) {
    return (
      <Box color={'{colors.textTertiary}'} p={'4px 0'}>
        {t('notes.loading')}
      </Box>
    );
  }

  return (
    <Stack h={'100%'} gap={'0'} p={isLessThan768 ? '0' : '12px 10px 0 10px'}>
      <Box flex={'1 1 auto'} overflowY={'auto'} p={'4px 0 20px'}>
        <Stack gap={'12px'}>
          <Flex gap={'12px'} flexDirection={'column'}>
            <Flex align={'center'} gap={'16px'} p={'0 0 16px 0'}>
              <Box color={'{colors.textSecondary}'}>{t('notes.fields.enabled')}</Box>
              <Controller
                control={control}
                name={'enabled'}
                render={({field}) => (
                  <Switch size={'lg'} checked={field.value} onChange={field.onChange} />
                )}
              />
            </Flex>
            <Box>
              <Box color={'{colors.textSecondary}'} mb={'6px'}>
                {t('notes.fields.maxNotes')}
              </Box>
              <Input
                size={'lg'}
                type={'number'}
                min={1}
                {...register('max_notes', {valueAsNumber: true})}
              />
            </Box>
            <Box>
              <Box color={'{colors.textSecondary}'} mb={'6px'}>
                {t('notes.fields.softLimit')}
              </Box>
              <Input
                size={'lg'}
                type={'number'}
                min={1}
                {...register('soft_limit', {valueAsNumber: true})}
              />
            </Box>
            <Box>
              <Box color={'{colors.textSecondary}'} mb={'6px'}>
                {t('notes.fields.maxNoteLength')}
              </Box>
              <Input
                size={'lg'}
                type={'number'}
                min={1}
                {...register('max_note_length', {valueAsNumber: true})}
              />
            </Box>
            <Box>
              <Box color={'{colors.textSecondary}'} mb={'6px'}>
                {t('notes.fields.ttlHours')}
              </Box>
              <Input
                size={'lg'}
                type={'number'}
                min={1}
                disabled={!ttlEnabled}
                {...register('ttl_hours', {valueAsNumber: true})}
              />
            </Box>
            <Flex align={'center'} gap={'16px'}>
              <Box color={'{colors.textSecondary}'}>{t('notes.fields.ttlEnabled')}</Box>
              <Controller
                control={control}
                name={'ttl_enabled'}
                render={({field}) => (
                  <Switch size={'lg'} checked={field.value} onChange={field.onChange} />
                )}
              />
            </Flex>
          </Flex>
        </Stack>
      </Box>

      <Flex
        pt={isLessThan768 ? '16px' : '24px'}
        justifyContent={'flex-end'}
        borderTop={'thin solid'}
        borderColor={'{colors.bgMain}'}
      >
        <Button
          variant={'solid'}
          size={isLessThan768 ? 'md' : 'lg'}
          disabled={!formState.isValid || updateMutation.isPending || !formState.isDirty}
          onClick={handleSubmit(onSubmit)}
        >
          {t('notes.actions.saveSettings')}
        </Button>
      </Flex>
    </Stack>
  );
};

export const NotesSettingsTab = NotesSettingsTabFC;
